import { User, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMoolah } from '../contexts/MoolahContext';
import { APP_CONFIG } from '../config/app-config';
import { getToken } from '../utils/authToken';
import logoSvg from '../assets/logo.svg';
import { RestrictedWalletCreditBanner } from './RestrictedWalletCreditBanner';

export function TopBanner() {
  const navigate = useNavigate();
  const { unreadCount, notifications, markRead, markAllRead, blockingNotification, respondToPaymentRequest, refreshNotifications } = useMoolah();
  const [open, setOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Honour the user's "Flash the bell" preference (Profile -> Notification Settings).
  // The red dot still shows for unread notifications; only the pulse animation is gated.
  const [flashBell, setFlashBell] = useState(true);
  const [acknowledgingIds, setAcknowledgingIds] = useState<Set<number>>(new Set());
  const [acknowledgingAll, setAcknowledgingAll] = useState(false);
  const [acknowledgeError, setAcknowledgeError] = useState('');

  // Allow runtime toggle without code changes:
  // localStorage.setItem('brand_wordmark', '1') -> use text wordmark
  // localStorage.removeItem('brand_wordmark')   -> use SVG logo
  const preferWordmark = typeof window !== 'undefined' && localStorage.getItem('brand_wordmark') === '1';
  const activeNotifications = notifications.filter((notification) => !notification.readAt);

  // Load the saved flash-bell preference once, and update live when the user
  // changes it on the Notification Settings page (dispatched window event).
  useEffect(() => {
    let cancelled = false;

    const loadFlashPref = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/notifications/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.success && data.data && typeof data.data.flashBell === 'boolean') {
          setFlashBell(data.data.flashBell);
        }
      } catch (_) {
        // Non-critical: default to flashing if preference cannot be read.
      }
    };

    loadFlashPref();

    const handleSettingsUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail && typeof detail.flashBell === 'boolean') {
        setFlashBell(detail.flashBell);
      }
    };

    window.addEventListener('notificationSettingsUpdated', handleSettingsUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('notificationSettingsUpdated', handleSettingsUpdate);
    };
  }, []);

  const acknowledgeNotification = async (id: number) => {
    if (acknowledgingIds.has(id)) return;
    setAcknowledgeError('');
    setAcknowledgingIds((current) => new Set(current).add(id));
    try {
      await markRead(id);
    } catch (_) {
      setAcknowledgeError('Could not acknowledge this notice. Please try again.');
    } finally {
      setAcknowledgingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };

  const acknowledgeAllNotifications = async () => {
    if (acknowledgingAll || activeNotifications.length === 0) return;
    setAcknowledgeError('');
    setAcknowledgingAll(true);
    try {
      await markAllRead();
    } catch (_) {
      setAcknowledgeError('Could not acknowledge all notices. Please try again.');
    } finally {
      setAcknowledgingAll(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <header 
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        width: '100%',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'block',
        minHeight: '64px',
        fontFamily: 'Montserrat, sans-serif'
      }}
    >
      <div 
        style={{
          maxWidth: '375px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px',
          padding: '0 16px'
        }}
      >
        {/* Left: Profile Icon */}
        <button 
          onClick={() => navigate('/profile')}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            padding: '0',
            minHeight: '44px',
            minWidth: '44px',
            fontFamily: 'Montserrat, sans-serif'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          aria-label="Profile and Settings"
        >
          <User style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </button>

        {/* Center: MyMoolah brand (SVG with graceful fallback to gradient wordmark) */}
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 16px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            fontFamily: 'Montserrat, sans-serif'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          aria-label="Home"
        >
          {(!preferWordmark && !logoFailed) ? (
            <img
              src={logoSvg}
              alt="MyMoolah"
              onError={() => setLogoFailed(true)}
              style={{
                height: 48, // Double the previous size (24px -> 48px)
                display: 'block'
              }}
            />
          ) : (
            <div 
              style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1f2937',
                fontFamily: 'Montserrat, sans-serif',
                background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              MyMoolah
            </div>
          )}
        </button>

        {/* Right: Notifications Icon */}
        <button 
          onClick={async () => {
            // Option 1: Auto-refresh notifications when bell is clicked
            await refreshNotifications();
            setOpen(true);
          }}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            transition: 'background-color 0.2s ease',
            padding: '0',
            minHeight: '44px',
            minWidth: '44px',
            fontFamily: 'Montserrat, sans-serif'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          <Bell style={{ width: '20px', height: '20px', color: '#6b7280' }} />
          {unreadCount > 0 && (
            <span 
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '10px',
                height: '10px',
                backgroundColor: '#dc2626',
                borderRadius: '50%',
                animation: flashBell ? 'pulse 1.2s infinite' : 'none'
              }}
            />
          )}
        </button>
      </div>

      {/* Restricted wallet credit banner — shown when the user has a balance
          but is below KYC Tier 1 (CP disbursement credited before KYC). */}
      <RestrictedWalletCreditBanner />

      {open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 55,
            animation: 'notifOverlayIn 0.2s ease-out'
          }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 375,
              margin: '0 auto',
              paddingTop: 64,
              height: '100%',
              pointerEvents: 'none'
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="notifications-dialog-title"
              style={{
                pointerEvents: 'auto',
                background: '#ffffff',
                borderRadius: '0 0 16px 16px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                maxHeight: 'calc(100vh - 140px)',
                display: 'flex',
                flexDirection: 'column',
                animation: 'notifSlideDown 0.25s ease-out',
                fontFamily: 'Montserrat, sans-serif',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px 10px',
                borderBottom: '1px solid #f1f5f9'
              }}>
                <h3 id="notifications-dialog-title" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>Notifications</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {activeNotifications.length > 0 && (
                    <button
                      onClick={acknowledgeAllNotifications}
                      disabled={acknowledgingAll}
                      style={{
                        minHeight: 36,
                        borderRadius: 999,
                        border: '1px solid #86BE41',
                        background: acknowledgingAll ? '#f3f4f6' : '#ffffff',
                        color: acknowledgingAll ? '#9ca3af' : '#4f8f21',
                        cursor: acknowledgingAll ? 'not-allowed' : 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                        padding: '0 12px',
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                      aria-label="Acknowledge all notifications"
                    >
                      {acknowledgingAll ? 'Acknowledging...' : 'Acknowledge all'}
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    style={{
                      width: 36, height: 36,
                      borderRadius: '50%',
                      border: 'none',
                      background: '#f3f4f6',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                      color: '#6b7280',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                    aria-label="Close notifications"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Body */}
              <div style={{ overflowY: 'auto', padding: '4px 0' }}>
                {acknowledgeError && (
                  <div
                    role="alert"
                    style={{
                      margin: '8px 16px',
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: '#fef2f2',
                      color: '#b91c1c',
                      fontSize: 12,
                      lineHeight: 1.4
                    }}
                  >
                    {acknowledgeError}
                  </div>
                )}
                {activeNotifications.length === 0 ? (
                  <div style={{
                    padding: '32px 16px',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <Bell style={{ width: 28, height: 28, margin: '0 auto 8px', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontSize: 14 }}>You're all caught up.</p>
                  </div>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {activeNotifications.map(n => {
                      const dotColor =
                        n.type === 'txn_bank_credit' || n.type === 'txn_wallet_credit'
                          ? '#2D8CCA'
                          : n.type === 'kyc_status'
                            ? '#6366f1'
                            : n.type === 'maintenance'
                              ? '#f59e0b'
                              : '#86BE41';
                      const isAcknowledging = acknowledgingIds.has(n.id);
                      return (
                        <li
                          key={n.id}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f8fafc',
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start',
                            transition: 'background 0.15s'
                          }}
                        >
                          <div style={{
                            width: 10, height: 10,
                            borderRadius: '50%',
                            backgroundColor: dotColor,
                            marginTop: 5,
                            flexShrink: 0
                          }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 2 }}>{n.title}</div>
                            {n.message && (
                              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4, marginBottom: 4 }}>{n.message}</div>
                            )}
                            <div style={{ fontSize: 11, color: '#d1d5db', marginBottom: 10 }}>{new Date(n.createdAt).toLocaleString('en-ZA')}</div>
                            <button
                              onClick={() => acknowledgeNotification(n.id)}
                              disabled={isAcknowledging || acknowledgingAll}
                              style={{
                                minHeight: 44,
                                minWidth: 120,
                                borderRadius: 10,
                                border: '1px solid #e5e7eb',
                                background: isAcknowledging || acknowledgingAll ? '#f3f4f6' : '#ffffff',
                                color: isAcknowledging || acknowledgingAll ? '#9ca3af' : '#1f2937',
                                cursor: isAcknowledging || acknowledgingAll ? 'not-allowed' : 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                padding: '0 12px',
                                fontFamily: 'Montserrat, sans-serif'
                              }}
                              aria-label={`Acknowledge notification: ${n.title}`}
                            >
                              {isAcknowledging ? 'Acknowledging...' : 'Acknowledge'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blocking overlay for payment requests (freezeUntilViewed) */}
      {blockingNotification && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ maxWidth: 360, margin: '0 16px', background: '#fff', borderRadius: 12, padding: 16, fontFamily: 'Montserrat, sans-serif' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 6 }}>{blockingNotification.title || 'Payment Request'}</h3>
            {blockingNotification.message && <p style={{ color: '#374151', marginTop: 0 }}>{blockingNotification.message}</p>}
            {blockingNotification.payload && (
              <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, margin: '12px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Amount</span>
                  <strong>R{Number(blockingNotification.payload.amount || 0).toFixed(2)}</strong>
                </div>
                {blockingNotification.payload.description && (
                  <div style={{ marginTop: 6, color: '#6b7280' }}>{blockingNotification.payload.description}</div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => respondToPaymentRequest(Number(blockingNotification.payload?.requestId), 'decline', blockingNotification.id)} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>Decline</button>
              <button onClick={() => respondToPaymentRequest(Number(blockingNotification.payload?.requestId), 'approve', blockingNotification.id)} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)', color: '#fff', cursor: 'pointer' }}>Approve</button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes pulse { 0%{ transform: scale(1); opacity: .8 } 50%{ transform: scale(1.3); opacity: 1 } 100%{ transform: scale(1); opacity: .8 } }
          @keyframes notifOverlayIn { from { opacity: 0 } to { opacity: 1 } }
          @keyframes notifSlideDown { from { opacity: 0; transform: translateY(-12px) } to { opacity: 1; transform: translateY(0) } }
        `}
      </style>
    </header>
  );
}